import { memo, useMemo, useCallback } from 'react';
import { CheckCircle, Clock, Hourglass, MessageCircle, XCircle } from 'lucide-react';
import {
    formatDuration, formatExactTime, formatTimeAgo
} from '@/components/agent/display/utils/formatters';
import { GenieAvatar } from '@/components/GenieAvatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Conversation } from '@/types/conversation/conversation';
import { TranscriptCopyButton } from './details/TranscriptCopyButton';
import { ConversationAttentionPill, extractConversationSignals } from './conversationListShared';

interface ConversationListItemProps {
  conversation: Conversation;
  onViewDetails: (conversation: Conversation) => void;
  isViewed?: boolean;
}

const getStatusBadge = (status?: string, successful?: string) => {
  if (successful === "successful" || status === "complete") {
    return { label: "Completed", icon: <CheckCircle className="w-3.5 h-3.5" />, color: "text-green-600" };
  }
  if (successful === "unsuccessful" || status === "ended_early") {
    return { label: "Ended", icon: <XCircle className="w-3.5 h-3.5" />, color: "text-red-600" };
  }
  return null;
};

export const ConversationListItem = memo(({ conversation, onViewDetails, isViewed = true }: ConversationListItemProps) => {
  const brandColor = conversation.agent?.branding?.primaryColor || "#4E9CFF";
  const secondaryColor = conversation.agent?.branding?.secondaryColor || brandColor;
  const logoUrl = conversation.agent?.branding?.logoUrl || conversation.agent?.branding?.customAvatarUrl;
  const hasGradient = conversation.agent?.branding?.gradientEnabled !== false;
  const gradientAngle = conversation.agent?.branding?.gradientAngle || 135;
  const statusBadge = getStatusBadge(conversation.details?.status, conversation.metadata?.call_successful);
  const { attentionBadge, isUrgent } = extractConversationSignals(conversation);
  const convTime = (() => {
    if (conversation.details?.conversation_date) {
      return conversation.details.conversation_date;
    }
    if (conversation.metadata?.start_time) {
      return new Date(conversation.metadata.start_time * 1000).toISOString();
    }
    return "";
  })();

  const timeAgo = formatTimeAgo(convTime);
  const exactTime = formatExactTime(convTime);
  const hasValidTime = timeAgo !== "Unknown" && exactTime !== "Unknown";

  const backgroundGradient = hasGradient ? `linear-gradient(${gradientAngle}deg, ${brandColor}, ${secondaryColor})` : brandColor;

  // Show time ago badge for recent conversations (less than 24 hours)
  const isRecent = (() => {
    if (!convTime) return false;
    try {
      const date = new Date(convTime + (convTime.includes("Z") ? "" : "Z"));
      return new Date().getTime() - date.getTime() < 24 * 60 * 60 * 1000;
    } catch {
      return false;
    }
  })();

  const handleClick = useCallback(() => {
    onViewDetails(conversation);
  }, [conversation, onViewDetails]);

  return (
    <div
      onClick={handleClick}
      className={cn(
        "relative bg-white rounded-3xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 group cursor-pointer",
        !isViewed && "ring-2 ring-offset-2 animate-pulse-subtle"
      )}
      style={{
        borderWidth: isUrgent || !isViewed ? "3px" : "2px",
        borderColor: isUrgent ? "#FF4DB8" : !isViewed ? brandColor : `${brandColor}40`,
        borderStyle: "solid",
        boxShadow: isUrgent ? "0 0 20px rgba(255,77,184,0.2)" : !isViewed ? `0 0 20px ${brandColor}20` : undefined,
      }}
    >
      <div className="p-6 sm:p-7 h-full flex flex-col">
        {/* Keep horizontal layout but make it mobile-friendly */}
        <div className="flex items-start gap-3 sm:gap-4 flex-1">
          {/* Genie Avatar - smaller on mobile, stays on left */}
          <div className="flex-shrink-0">
            <div
              className="relative rounded-full p-2 sm:p-3"
              style={{
                background: `radial-gradient(circle, ${brandColor}15 0%, ${brandColor}05 70%, transparent 100%)`,
              }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={conversation.agent?.name || "Agent"}
                  className="w-10 h-10 sm:w-14 sm:h-14 object-contain"
                  style={{
                    filter: `drop-shadow(0 0 6px ${brandColor}40)`,
                  }}
                />
              ) : (
                <div className="w-10 h-10 sm:w-14 sm:h-14">
                  <GenieAvatar
                    size={56}
                    color={brandColor}
                    secondaryColor={secondaryColor}
                    gradientEnabled={hasGradient}
                    gradientAngle={gradientAngle}
                    customAvatarUrl={logoUrl}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Header with name and badges */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="min-w-0 truncate font-semibold text-gray-900 text-base sm:text-lg leading-tight">
                    {conversation.agent?.name || "Unknown Agent"}
                  </h3>
                  {!conversation.isSynced && (
                    <Badge className="text-xs py-0 px-1.5 text-white bg-orange-500 border-orange-600">
                      <Hourglass className="w-3 h-3 mr-1" />
                      Not Analyzed
                    </Badge>
                  )}
                  {!isViewed && (
                    <Badge
                      className="text-xs py-0 px-1.5 text-white animate-pulse"
                      style={{
                        backgroundColor: brandColor,
                        boxShadow: `0 0 10px ${brandColor}40`,
                      }}
                    >
                      NEW
                    </Badge>
                  )}
                </div>

                {/* Time and recent badge */}
                {hasValidTime && (
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <p className="text-sm text-gray-600">
                      {timeAgo} at {exactTime}
                    </p>
                    {attentionBadge && <ConversationAttentionPill attention={attentionBadge} />}
                    {isRecent && timeAgo !== "Just now" && isViewed && (
                      <Badge variant="secondary" className="text-xs py-0 px-1.5 bg-blue-50 text-blue-600 border-blue-200">
                        Recent
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Status badge - always visible on the right */}
              {statusBadge && (
                <div className={cn("flex items-center gap-1 text-xs font-medium shrink-0", statusBadge.color)}>
                  {statusBadge.icon}
                  <span className="hidden sm:inline">{statusBadge.label}</span>
                  <span className="sm:hidden">{statusBadge.icon}</span>
                </div>
              )}
            </div>

            {/* Info Row - Wrappable on mobile */}
            <div className="flex items-center gap-2 sm:gap-3 text-sm text-gray-500 flex-wrap">
              {(conversation.details?.duration || conversation.metadata?.duration_seconds) && (
                <div className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{formatDuration(conversation.details?.duration || conversation.metadata?.duration_seconds || 0, true)}</span>
                </div>
              )}
              {conversation.metadata?.message_count && conversation.metadata.message_count > 0 && (
                <div className="flex items-center gap-1.5">
                  <MessageCircle className="w-4 h-4" />
                  <span className="font-medium">{conversation.metadata.message_count}</span>
                </div>
              )}
              <div className="ml-auto">
                <TranscriptCopyButton conversation={conversation} compact />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ConversationListItem.displayName = 'ConversationListItem';
